import { useState, useEffect } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate } from "react-router-dom";
import { IconButton, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ModuleLayout from "./ModuleLayout/ModuleLayout";
import { teamValidationSchema } from "./FormModal/validationSchemas";

function Teams() {
  const navigate = useNavigate();

  // Team initial values
  const teamInit = {
    name: "",
    email: "",
    password: "",
    parent_id: Helpers.authParentId,
    user_role: null,
  };

  const [rolesOptions, setRolesOptions] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

    useEffect(() => {
    const getRoles = async () => {
      try {
        const response = await axios.get(
          `${Helpers.baseUrl}/roles/all/${Helpers.authParentId}`,
          Helpers.headers,
        );

        const rolesData = Array.isArray(response.data) ? response.data : [];
        const roleArray = rolesData.map((role) => ({
          label: role.name,
          value: role._id,
        }));

        setRolesOptions(roleArray);
        setRolesLoaded(true);
      } catch (error) {
        Helpers.unauthenticated(error, navigate);
        setRolesLoaded(true);
      }
    };

    getRoles();
  }, [navigate]); 

  // Team form fields - dynamically uses rolesOptions
  const teamFields = [
    { name: "name", label: "Name", type: "text" },
    { name: "email", label: "Email", type: "email" },
    {
      name: "password",
      label: "Password",
      type: "password",
      helperText: "Leave blank to keep existing password when editing",
    },
    {
      name: "user_role",
      label: "User Role",
      type: "select",
      options: rolesOptions,
      disabled: !rolesLoaded,
    },
  ];

  // Team search filter
  const teamSearchFilter = (team, search) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (team.name || "").toLowerCase().includes(term) ||
      (team.email || "").toLowerCase().includes(term) ||
      (team.user_role?.name || "").toLowerCase().includes(term)
    );
  };

  // Sort function
  const teamSortFunction = (a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    });

  // Get item name function
  const getTeamName = (team) => team?.name;

  // Before save transformation
  const onBeforeSave = (values) => {
    // If password is empty during edit, remove it from the payload
    if (!values.password) {
      const { password, ...rest } = values;
      return rest;
    }
    return values;
  };

  // After fetch transformation - keep it simple
  const onAfterFetch = (data) => {
    return data;
  };

  // Team columns - using valueGetter for better performance
  const teamColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "user_role",
      headerName: "Role",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.user_role?.name || "",
    },
  ];

  const customActionsRenderer = (
    params,
    canUpdate,
    canDelete,
    handleEdit,
    handleDelete,
  ) => {
    // Transform the row data for editing
    const transformedRow = {
      ...params.row,
      user_role: params.row.user_role?._id || params.row.user_role || null,
      password: "",
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          gap: 1,
        }}
      >
        {canUpdate && (
          <IconButton
            onClick={() => handleEdit(transformedRow)}
            color="success"
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            onClick={() => handleDelete(params.row)}
            color="error"
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };


  return (
    <ModuleLayout
      moduleName="Teams"
      moduleKey="teams"
      icon={require("../images/icons/teams.png")}
      initialFormValues={teamInit}
      formFields={teamFields}
      validationSchema={teamValidationSchema}
      columns={teamColumns}
      searchFilter={teamSearchFilter}
      sortFunction={teamSortFunction}
      getItemName={getTeamName}
      customActionsRenderer={customActionsRenderer}
      onBeforeSave={onBeforeSave}
      onAfterFetch={onAfterFetch}
      deleteWarning="This action cannot be undone. The team member will lose access to the system."
      moduleDescription="Manage team members and their role assignments"
    />
  );
}

export default Teams;
